import { useRef, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { trackEvent } from '@/utils/analytics/useAnalytics'
import { useTranslation } from 'react-i18next'
import { useOnClickOutside } from 'usehooks-ts'

import { useDisclosure } from '@chakra-ui/react'
import { AnimatePresence } from 'framer-motion'

import {
  AlertTriangleIcon,
  BellIcon,
  BellOffIcon,
  Box,
  DarkProvider,
  Flex,
  Grid,
  MailIcon,
  Text,
  Tooltip,
} from '@hivewatch/dark'

import { useIncidentAudio } from '@/features/account-information'
import { useResetNotificationCountMutation } from '@/graphql/appsync-notifications'
import { useGlobalUnresolvedIncidentsCountQuery } from '@/graphql/generated/hooks'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  selectAreNotificationsMuted,
  setAreNotificationsMuted,
  setNotifications,
} from '@/redux/notifications/notificationsSlice'
import { BUTTON_PRESS } from '@/utils/analytics'

import { InboxDrawer } from '../inbox-drawer'
import { IncidentsDrawer } from '../incidents-drawer'
import { getNotificationsTabTokens } from '../styles/tokens'
import { DrawerToggleButton } from './DrawerToggleButton'
import { InboxItemsCount } from './InboxItemsCount'
import { IncidentsCount } from './IncidentsCount'

export const NotificationsTab = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const tokens = getNotificationsTabTokens(colorScheme)
  const { data: totalCountData } = useGlobalUnresolvedIncidentsCountQuery()
  const [resetNotificationCount] = useResetNotificationCountMutation({
    errorPolicy: 'all',
  })
  // A muted bell still lets a new Critical incident play its sound only when
  // the organization's Critical-sound policy is on (ENG-35923). The policy
  // needs incidentAudioAlerts, which gates all incident audio (see
  // useIncidentAudio.ts's playAlertSound). The tooltip must not promise a
  // sound that won't play.
  const { isCriticalSoundPolicyActive: isCriticalBypassEnabled } =
    useIncidentAudio()
  const areNotificationsMuted = useAppSelector(selectAreNotificationsMuted)
  const tooltipLabel = areNotificationsMuted
    ? t(
        isCriticalBypassEnabled
          ? 'common:notification_muted_critical_bypass'
          : 'common:notification_muted'
      )
    : t('common:notification_enabled')

  const totalCount = totalCountData?.globalUnresolvedIncidentsCount?.totalCount
  const {
    isOpen: isIncidentsDrawerOpen,
    onOpen: onIncidentsDrawerOpen,
    onClose: onIncidentsDrawerClose,
  } = useDisclosure()

  const {
    isOpen: isInboxDrawerOpen,
    onOpen: onInboxDrawerOpen,
    onClose: onInboxDrawerClose,
  } = useDisclosure()

  const [bellAnchorEl, setBellAnchorEl] = useState<HTMLElement | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  useOnClickOutside(ref, () => {
    onIncidentsDrawerClose()
    onInboxDrawerClose()
  })

  const toggleInboxDrawer = async () => {
    onIncidentsDrawerClose()
    if (isInboxDrawerOpen) {
      onInboxDrawerClose()
    } else {
      onInboxDrawerOpen()
      await resetNotificationCount()
    }
  }

  const toggleIncidentsDrawer = () => {
    onInboxDrawerClose()
    isIncidentsDrawerOpen ? onIncidentsDrawerClose() : onIncidentsDrawerOpen()
  }

  // General open-close button
  const toggleDrawers = () => {
    if (isIncidentsDrawerOpen || isInboxDrawerOpen) {
      onInboxDrawerClose()
      onIncidentsDrawerClose()
    } else {
      onIncidentsDrawerOpen()
    }
  }

  const handleBellClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    // New state = toggle whatever old state was
    const shouldPopUpsBeMuted = !areNotificationsMuted
    if (shouldPopUpsBeMuted) {
      dispatch(setNotifications([]))
    }
    dispatch(setAreNotificationsMuted(shouldPopUpsBeMuted))
    trackEvent(`${BUTTON_PRESS} Nav Drawer - Mute Button`, {
      isNowMuted: shouldPopUpsBeMuted,
    })
  }

  return (
    <DarkProvider colorScheme={colorScheme}>
      <Box bottom='0' pos='fixed' ref={ref} right='0' w='450px' zIndex={10}>
        <AnimatePresence exitBeforeEnter>
          {isIncidentsDrawerOpen && <IncidentsDrawer totalCount={totalCount} />}
          {isInboxDrawerOpen && <InboxDrawer />}
        </AnimatePresence>
        <Grid
          bg={tokens.surface}
          border='1px solid'
          borderColor={tokens.border}
          borderRadius='none'
          h='42px'
          overflow='hidden'
          templateColumns='42px 250px auto 50px'
        >
          <Flex
            _hover={{ opacity: 0.9 }}
            alignItems='center'
            bgColor={tokens.incidentBg}
            color={tokens.incidentFg}
            cursor='pointer'
            data-testid='NotificationsTab:activeIncidentsIcon'
            flexDirection='column'
            justifyContent='center'
            onClick={toggleIncidentsDrawer}
            transition='opacity 300ms ease-in-out'
          >
            <AlertTriangleIcon color='currentColor' size={22} />
          </Flex>
          <Flex
            // While the bell (mute toggle) is hovered, suppress this cell's own
            // hover background so hovering the bell doesn't read as hovering the
            // whole "Active Incidents" cell (replaces the legacy
            // `:has(.bellIcon:hover)` guard).
            _hover={{ bgColor: bellAnchorEl ? undefined : tokens.rowHover }}
            alignItems='center'
            bgColor={isIncidentsDrawerOpen ? tokens.rowHover : tokens.surface}
            borderColor={tokens.border}
            borderRightWidth='1px'
            cursor='pointer'
            data-testid='NotificationsTab:activeIncidents'
            gap='2'
            justifyContent='start'
            onClick={toggleIncidentsDrawer}
            px='3'
            transition='background-color 300ms ease-in-out'
          >
            <Text
              color={tokens.textPrimary}
              data-testid='notificationsTab_activeIncidents_title'
              lineClamp={1}
              style={{ lineBreak: 'anywhere' }}
              textStyle='caption-medium'
            >
              {t('common:notification_active_incidents')}
            </Text>
            <IncidentsCount />
            <Flex color={tokens.icon} flex={1} justifyContent='flex-end'>
              <Flex
                _hover={{ bgColor: tokens.rowHover }}
                aria-label={tooltipLabel}
                borderRadius='none'
                justifyContent='flex-end'
                onClick={handleBellClick}
                onMouseEnter={(e) => setBellAnchorEl(e.currentTarget)}
                onMouseLeave={() => setBellAnchorEl(null)}
                p={1.5}
              >
                {areNotificationsMuted ? (
                  <BellOffIcon color='currentColor' size={20} />
                ) : (
                  <BellIcon color='currentColor' size={20} />
                )}
              </Flex>
              <Tooltip
                anchorEl={bellAnchorEl}
                colorScheme={colorScheme}
                label={tooltipLabel}
                placement='top'
              />
            </Flex>
          </Flex>
          <Grid
            _hover={{ bgColor: tokens.rowHover }}
            alignItems='center'
            bgColor={isInboxDrawerOpen ? tokens.rowHover : tokens.surface}
            borderColor={tokens.border}
            borderRightWidth='1px'
            color={tokens.icon}
            cursor='pointer'
            data-testid='notificationsTab_inbox_title'
            gap='2'
            justifyContent='center'
            onClick={toggleInboxDrawer}
            px='3'
            templateColumns='auto auto'
            transition='background-color 300ms ease-in-out'
          >
            <MailIcon color='currentColor' size={22} />
            <InboxItemsCount />
          </Grid>
          <Flex
            _hover={{ bgColor: tokens.rowHover }}
            alignItems='center'
            cursor='pointer'
            flexDirection='column'
            justifyContent='center'
            onClick={toggleDrawers}
            position='relative'
            px='3'
            transition='background-color 300ms ease-in-out'
          >
            <DrawerToggleButton
              isOpen={isIncidentsDrawerOpen || isInboxDrawerOpen}
            />
          </Flex>
        </Grid>
      </Box>
    </DarkProvider>
  )
}
